import { createContext, useCallback, useContext, useEffect, useState, ReactNode } from "react";
import { signOut as signOutHelper } from "../lib/auth-helpers";
import type { AuthSession, AuthUser } from "../lib/auth-client";
import { auth } from "../lib/auth-client";

// --- Types ---

interface AuthState {
    isAuthenticated: boolean;
    userId: string | null;
    user: AuthUser | null;
    accessToken: string | null;
    session: AuthSession | null;
}

interface AuthContextType extends AuthState {
    isLoading: boolean;
    error: Error | null;
    signOut: () => Promise<void>;
    refreshSession: () => Promise<void>;
}

// --- Context ---

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuthContext = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuthContext must be used within an AuthProvider");
    }
    return context;
};

// --- Provider ---

export const AuthProvider = ({ children }: { children: ReactNode }) => {
    const [authState, setAuthState] = useState<AuthState>({
        isAuthenticated: false,
        userId: null,
        user: null,
        accessToken: null,
        session: null,
    });
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    // Initialize auth state
    useEffect(() => {
        let mounted = true;

        const initializeAuth = async () => {
            try {
                const { data: { session }, error: sessionError } = await auth.getSession();

                if (sessionError) {
                    throw sessionError;
                }

                if (mounted) {
                    setAuthState({
                        isAuthenticated: !!session,
                        userId: session?.user?.id || null,
                        user: session?.user || null,
                        accessToken: session?.access_token || null,
                        session: session,
                    });
                    setIsLoading(false);
                }
            } catch (err) {
                if (mounted) {
                    setError(err instanceof Error ? err : new Error("Failed to initialize auth"));
                    setIsLoading(false);
                }
            }
        };

        initializeAuth();

        // Set up auth state listener
        const {
            data: { subscription },
        } = auth.onAuthStateChange((_event, session) => {
            if (mounted) {
                setAuthState({
                    isAuthenticated: !!session,
                    userId: session?.user?.id || null,
                    user: session?.user || null,
                    accessToken: session?.access_token || null,
                    session: session,
                });
                setError(null);
            }
        });

        return () => {
            mounted = false;
            subscription.unsubscribe();
        };
    }, []);

    const signOut = useCallback(async () => {
        try {
            await signOutHelper();
        } catch (err) {
            setError(err instanceof Error ? err : new Error("Failed to sign out"));
            throw err;
        }
    }, []);

    const refreshSession = useCallback(async () => {
        try {
            setIsLoading(true);
            const { data: { session }, error } = await auth.refreshSession();

            if (error) throw error;

            setAuthState({
                isAuthenticated: !!session,
                userId: session?.user?.id || null,
                user: session?.user || null,
                accessToken: session?.access_token || null,
                session: session,
            });
        } catch (err) {
            setError(err instanceof Error ? err : new Error("Failed to refresh session"));
            throw err;
        } finally {
            setIsLoading(false);
        }
    }, []);

    const value: AuthContextType = {
        ...authState,
        isLoading,
        error,
        signOut,
        refreshSession,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
